import React from 'react';
import { Link } from 'react-router-dom';
import { Home, ArrowLeft, Search, Mail, ExternalLink } from 'lucide-react';
import Navbar from '../components/Navabar';
import Footer from '../components/Footer';

const NotFoundPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <Navbar />
      
      {/* Page 404 Content */}
      <div className="relative min-h-screen flex items-center justify-center px-4 py-20">
        {/* Molecular Background Pattern */}
        <div className="absolute inset-0 overflow-hidden">
          <svg className="absolute w-full h-full" viewBox="0 0 1400 800" preserveAspectRatio="none">
            <defs>
              <radialGradient id="molecule404" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="#089556" stopOpacity="0.1"/>
                <stop offset="100%" stopColor="#089556" stopOpacity="0.05"/>
              </radialGradient>
              <radialGradient id="molecule404-2" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="#07D1EB" stopOpacity="0.1"/>
                <stop offset="100%" stopColor="#07D1EB" stopOpacity="0.05"/>
              </radialGradient>
            </defs>
            
            {/* Animated Molecules */}
            <circle cx="200" cy="150" r="30" fill="url(#molecule404)">
              <animate attributeName="cy" values="150;120;150" dur="6s" repeatCount="indefinite"/>
              <animate attributeName="opacity" values="0.3;0.6;0.3" dur="6s" repeatCount="indefinite"/>
            </circle>
            <circle cx="400" cy="300" r="25" fill="url(#molecule404-2)">
              <animate attributeName="cy" values="300;280;300" dur="8s" repeatCount="indefinite"/>
              <animate attributeName="opacity" values="0.4;0.7;0.4" dur="8s" repeatCount="indefinite"/>
            </circle>
            <circle cx="800" cy="200" r="35" fill="url(#molecule404)">
              <animate attributeName="cy" values="200;180;200" dur="7s" repeatCount="indefinite"/>
              <animate attributeName="opacity" values="0.2;0.5;0.2" dur="7s" repeatCount="indefinite"/>
            </circle>
            <circle cx="1100" cy="400" r="28" fill="url(#molecule404-2)">
              <animate attributeName="cy" values="400;370;400" dur="9s" repeatCount="indefinite"/>
              <animate attributeName="opacity" values="0.3;0.6;0.3" dur="9s" repeatCount="indefinite"/>
            </circle>
            <circle cx="600" cy="500" r="32" fill="url(#molecule404)">
              <animate attributeName="cy" values="500;480;500" dur="5s" repeatCount="indefinite"/>
              <animate attributeName="opacity" values="0.4;0.8;0.4" dur="5s" repeatCount="indefinite"/>
            </circle>
            
            {/* Connecting Lines */}
            <line x1="200" y1="150" x2="400" y2="300" stroke="#089556" strokeWidth="1" opacity="0.2">
              <animate attributeName="opacity" values="0.2;0.5;0.2" dur="6s" repeatCount="indefinite"/>
            </line>
            <line x1="400" y1="300" x2="800" y2="200" stroke="#07D1EB" strokeWidth="1" opacity="0.3">
              <animate attributeName="opacity" values="0.3;0.6;0.3" dur="8s" repeatCount="indefinite"/>
            </line>
            <line x1="800" y1="200" x2="1100" y2="400" stroke="#089556" strokeWidth="1" opacity="0.2">
              <animate attributeName="opacity" values="0.2;0.4;0.2" dur="7s" repeatCount="indefinite"/>
            </line>
            <line x1="600" y1="500" x2="800" y2="200" stroke="#07D1EB" strokeWidth="1" opacity="0.3">
              <animate attributeName="opacity" values="0.3;0.7;0.3" dur="5s" repeatCount="indefinite"/>
            </line>
          </svg>
        </div>

        {/* Main Content */}
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          {/* 404 Number */}
          <div className="mb-8">
            <div className="text-[12rem] md:text-[16rem] font-bold text-transparent bg-gradient-to-r from-[#089556] to-[#07D1EB] bg-clip-text leading-none">
              404
            </div>
            <div className="text-2xl md:text-3xl font-semibold text-gray-700 -mt-8">
              Page non trouvée
            </div>
          </div>

          {/* Error Message */}
          <div className="mb-12">
            <h1 className="text-xl md:text-2xl font-semibold text-gray-800 mb-4">
              Oups ! Cette page n'existe pas
            </h1>
            <p className="text-gray-600 text-lg leading-relaxed max-w-2xl mx-auto">
              La page que vous recherchez a peut-être été déplacée, supprimée ou n'existe pas. 
              Mais ne vous inquiétez pas, nous sommes là pour vous aider à trouver ce que vous cherchez !
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <Link 
              to="/"
              className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-[#089556] to-[#07D1EB] text-white rounded-lg font-medium hover:shadow-lg transition-all duration-200 transform hover:scale-105"
            >
              <Home className="w-5 h-5 mr-2" />
              Retour à l'accueil
            </Link>
            
            <button 
              onClick={() => window.history.back()}
              className="inline-flex items-center px-6 py-3 bg-white text-[#089556] border border-[#089556] rounded-lg font-medium hover:bg-[#089556] hover:text-white transition-all duration-200"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Page précédente
            </button>
          </div>

          {/* Quick Links */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 border border-gray-200 hover:shadow-lg transition-all duration-200">
              <div className="w-12 h-12 bg-gradient-to-br from-[#089556] to-[#07D1EB] rounded-lg flex items-center justify-center mb-4 mx-auto">
                <Search className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Rechercher</h3>
              <p className="text-gray-600 text-sm mb-4">Utilisez notre barre de recherche pour trouver ce que vous cherchez</p>
              <Link to="/" className="text-[#089556] hover:text-[#07D1EB] font-medium text-sm">
                Aller à l'accueil →
              </Link>
            </div>

            <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 border border-gray-200 hover:shadow-lg transition-all duration-200">
              <div className="w-12 h-12 bg-gradient-to-br from-[#089556] to-[#07D1EB] rounded-lg flex items-center justify-center mb-4 mx-auto">
                <Mail className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Nous contacter</h3>
              <p className="text-gray-600 text-sm mb-4">Notre équipe est là pour vous aider</p>
              <Link to="/contacts" className="text-[#089556] hover:text-[#07D1EB] font-medium text-sm">
                Page contact →
              </Link>
            </div>

            <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 border border-gray-200 hover:shadow-lg transition-all duration-200">
              <div className="w-12 h-12 bg-gradient-to-br from-[#089556] to-[#07D1EB] rounded-lg flex items-center justify-center mb-4 mx-auto">
                <ExternalLink className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Nos actions</h3>
              <p className="text-gray-600 text-sm mb-4">Découvrez nos projets humanitaires</p>
              <Link to="/projets" className="text-[#089556] hover:text-[#07D1EB] font-medium text-sm">
                Voir nos projets →
              </Link>
            </div>
          </div>

          {/* Popular Links */}
          <div className="bg-white/60 backdrop-blur-sm rounded-xl p-6 border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Pages populaires</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Link to="/about" className="text-[#089556] hover:text-[#07D1EB] hover:underline transition-colors">
                À propos
              </Link>
              <Link to="/mission" className="text-[#089556] hover:text-[#07D1EB] hover:underline transition-colors">
                Notre mission
              </Link>
              <Link to="/projets" className="text-[#089556] hover:text-[#07D1EB] hover:underline transition-colors">
                Nos projets
              </Link>
              <Link to="/actualites" className="text-[#089556] hover:text-[#07D1EB] hover:underline transition-colors">
                Actualités
              </Link>
              <Link to="/publications" className="text-[#089556] hover:text-[#07D1EB] hover:underline transition-colors">
                Publications
              </Link>
              <Link to="/equipe" className="text-[#089556] hover:text-[#07D1EB] hover:underline transition-colors">
                Notre équipe
              </Link>
              <Link to="/don" className="text-[#089556] hover:text-[#07D1EB] hover:underline transition-colors">
                Faire un don
              </Link>
              <Link to="/contacts" className="text-[#089556] hover:text-[#07D1EB] hover:underline transition-colors">
                Contacts
              </Link>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default NotFoundPage; 